import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    PackageX, Building2, Search, Plus, Trash2, X, RefreshCw, Send,
    ChevronDown, Loader2, Layers, Paperclip,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { uploadFileToS3 } from '../../api/commonAPI/awsUploadAPI';
import { S3_FOLDERS } from '../../config/s3Config';

import {
    fetchCostCenters,
    fetchCategories,
    fetchMainGrid,
    fetchSuggestions,
    fetchLotDetails,
    addTempLDItems,
    deleteLDItem,
    submitLostOrDamaged,
    clearCategories,
    clearMainGrid,
    clearSuggestions,
    clearLotDetails,
    clearAddTempResult,
    clearSubmitResult,
    resetLostOrDamagedItems,
    selectLDCCList,
    selectLDCategoryList,
    selectLDMainGridItems,
    selectLDSuggestions,
    selectLDLotDetails,
    selectLDAddTempResult,
    selectLDSubmitResult,
    selectLDLoading,
    selectLDErrors,
} from '../../slices/purchaseSlice/lostOrDamagedItemsSlice';

// ── Shared UI (mirrors DailyIssue) ──────────────────────────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
        purple: 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const formatDateForSP = (d) => {
    if (!d) return '';
    if (d instanceof Date) {
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
    }
    return String(d);
};

const SELECTION_TYPES = [
    { value: '1', label: 'Search By Item Code' },
    { value: '2', label: 'Search By Item Name' },
];

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));
const isAssetCode = (code) => (code || '').trim().charAt(0) === '1';

const LostOrDamagedItems = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';
    const roleId = userData?.roleId || userData?.RID || 0;
    const uid = userData?.UID || userData?.uid || '';

    const ccList        = useSelector(selectLDCCList);
    const categoryList  = useSelector(selectLDCategoryList);
    const mainGridItems = useSelector(selectLDMainGridItems);
    const suggestions   = useSelector(selectLDSuggestions);
    const lotDetails    = useSelector(selectLDLotDetails);
    const addTempResult = useSelector(selectLDAddTempResult);
    const submitResult  = useSelector(selectLDSubmitResult);
    const loading        = useSelector(selectLDLoading);
    const errors          = useSelector(selectLDErrors);

    const [selectedCC, setSelectedCC] = useState('');

    const [category, setCategory] = useState('');
    const [selectionType, setSelectionType] = useState('');
    const [searchText, setSearchText] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchBoxRef = useRef(null);

    const [lotQtyEdits, setLotQtyEdits] = useState({}); // { [TempId]: { lost, damaged } }
    const [currentLotItemCode, setCurrentLotItemCode] = useState('');

    const [checkedRows, setCheckedRows] = useState({});
    const [stockTypeEdits, setStockTypeEdits] = useState({});
    const [remarksEdits, setRemarksEdits] = useState({});

    const [reportDate, setReportDate] = useState(todayFormatted());
    const [userRemarks, setUserRemarks] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (uid && roleId) dispatch(fetchCostCenters({ uid, rid: roleId }));
        return () => dispatch(resetLostOrDamagedItems());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggestions(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    useEffect(() => {
        if (!selectedCC || !category || !selectionType || !searchText.trim()) {
            dispatch(clearSuggestions());
            return;
        }
        const t = setTimeout(() => {
            dispatch(fetchSuggestions({ pfx: searchText.trim(), typ: selectionType, cat: category, cc: selectedCC }));
            setShowSuggestions(true);
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, selectionType, category, selectedCC]);

    // Reset per-lot qty edits whenever a fresh lot grid loads
    useEffect(() => {
        setLotQtyEdits({});
    }, [lotDetails]);

    // Reset per-row edits whenever the main grid changes (mirrors the legacy page's
    // full HTML-replace on every reload)
    useEffect(() => {
        setCheckedRows({}); setStockTypeEdits({}); setRemarksEdits({});
    }, [mainGridItems]);

    useEffect(() => {
        if (addTempResult) {
            toast.success(`Item Code :- '${addTempResult.code || currentLotItemCode}' Added Successfully`);
            dispatch(clearAddTempResult());
            setSearchText('');
            setShowSuggestions(false);
            if (selectedCC) dispatch(fetchMainGrid({ ccode: selectedCC }));
        } else if (errors.addTemp) {
            toast.error(errors.addTemp);
            dispatch(clearAddTempResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addTempResult, errors.addTemp]);

    useEffect(() => {
        if (submitResult) {
            (async () => {
                if (file && submitResult.code) {
                    setUploading(true);
                    try { await uploadFileToS3(file, S3_FOLDERS.LOST_DAMAGED_ITEMS, submitResult.code); }
                    catch { toast.warn('Report saved, but the document upload failed. Please retry the upload separately.'); }
                    finally { setUploading(false); }
                }
                toast.success('Items Added Successfully.');
                dispatch(clearSubmitResult());
                handleReset();
            })();
        } else if (errors.submit) {
            toast.error(errors.submit);
            dispatch(clearSubmitResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitResult, errors.submit]);

    // ── Cost Center ──────────────────────────────────────────────────────────

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        setCategory(''); setSelectionType(''); setSearchText('');
        dispatch(clearSuggestions());
        dispatch(clearLotDetails());
        dispatch(clearMainGrid());
        dispatch(clearCategories());
        if (cc) {
            dispatch(fetchCategories(cc));
            dispatch(fetchMainGrid({ ccode: cc }));
        }
    };

    // ── Item search / lot lookup ────────────────────────────────────────────

    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setSelectionType('');
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handleSelectionTypeChange = (e) => {
        if (!category) { toast.error('Select Search By Category'); return; }
        setSelectionType(e.target.value);
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handlePickSuggestion = (s) => {
        setSearchText(s.SearchItem || '');
        setShowSuggestions(false);
    };

    const handleClearSearch = () => {
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handleViewLots = () => {
        if (!category)      { toast.error('Select Search By Category'); return; }
        if (!selectionType) { toast.error('Select Search By Type'); return; }
        if (!searchText.trim()) { toast.error('Enter Search Data'); return; }
        if (!selectedCC)     { toast.error('Select Cost Center'); return; }

        const itemCode = searchText.split(',')[0].trim();
        setCurrentLotItemCode(itemCode);
        dispatch(fetchLotDetails({ itemcode: itemCode, cccode: selectedCC }));
    };

    const handleHideLots = () => {
        dispatch(clearLotDetails());
        handleClearSearch();
    };

    // ── Lot grid qty edits ───────────────────────────────────────────────────
    // Ported from the legacy per-keystroke validation in NewLDItemsIssueTempDetails.cshtml
    // (the *correct* implementation — the page-level re-check at Add-button time reads
    // `.text()` on cells that only contain an `<input>`, which is always empty and makes
    // that second check a no-op; we don't reproduce that dead code).

    const validateLotQty = (row, lost, damaged) => {
        const avlQty = num(row.TempQuantity);
        const actQty = num(row.ACCQty);
        const total = num(lost) + num(damaged);
        const asset = isAssetCode(row.TempItemcode);

        if (row.Tblname === 'ClientMasterdata') {
            if (total !== avlQty) { toast.error('Invalid Quantity'); return false; }
        } else if (row.Tblname === 'Masterdata' && asset) {
            if (num(lost) > 0 && num(lost) < 1)     { toast.error('Invalid Quantity'); return false; }
            if (num(damaged) > 0 && num(damaged) < 1) { toast.error('Invalid Quantity'); return false; }
        } else {
            if (total > avlQty || total > actQty) { toast.error('Invalid Quantity'); return false; }
        }
        return true;
    };

    const handleLotQtyChange = (row, field, value) => {
        setLotQtyEdits((prev) => {
            const current = prev[row.TempId] || { lost: '', damaged: '' };
            const next = { ...current, [field]: value };
            if (value !== '' && !validateLotQty(row, next.lost, next.damaged)) {
                return { ...prev, [row.TempId]: { ...current, [field]: '' } };
            }
            return { ...prev, [row.TempId]: next };
        });
    };

    const lotRowTotal = (row) => {
        const e = lotQtyEdits[row.TempId] || {};
        return num(e.lost) + num(e.damaged);
    };

    const lotTotals = useMemo(() => lotDetails.reduce((acc, row) => {
        const e = lotQtyEdits[row.TempId] || {};
        acc.lost += num(e.lost);
        acc.damaged += num(e.damaged);
        acc.total += lotRowTotal(row);
        return acc;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, { lost: 0, damaged: 0, total: 0 }), [lotDetails, lotQtyEdits]);

    const handleAddToIssue = () => {
        if (!selectedCC) { toast.error('Select Cost Center'); return; }

        const rowsToAdd = lotDetails.filter((row) => lotRowTotal(row) > 0);
        if (rowsToAdd.length === 0) {
            toast.error('Please Add Items to Issue');
            return;
        }
        for (const row of rowsToAdd) {
            const e = lotQtyEdits[row.TempId] || {};
            if (!validateLotQty(row, e.lost, e.damaged)) return;
        }

        let rowids = '', itemcodes = '', lqtys = '', dqtys = '', totalqtys = '', tblnames = '';
        rowsToAdd.forEach((row) => {
            const e = lotQtyEdits[row.TempId] || {};
            rowids    += `${row.TempId},`;
            itemcodes += `${row.TempItemcode},`;
            lqtys     += `${num(e.lost)},`;
            dqtys     += `${num(e.damaged)},`;
            totalqtys += `${lotRowTotal(row)},`;
            tblnames  += `${row.Tblname},`;
        });

        dispatch(addTempLDItems({
            Trowids: rowids,
            TItemCodes: itemcodes,
            TNewLQtys: lqtys,
            TNewDQtys: dqtys,
            TNewTotalQtys: totalqtys,
            Ttblnames: tblnames,
            TCCCode: selectedCC,
            TSingleItemcode: currentLotItemCode,
        }));
    };

    // ── Main grid ────────────────────────────────────────────────────────────

    const toggleRowChecked = (itemcode) => setCheckedRows((prev) => ({ ...prev, [itemcode]: !prev[itemcode] }));

    const handleDeleteRow = (item) => {
        if (!checkedRows[item.itemcode]) {
            toast.error('Please Check for Delete');
            return;
        }
        dispatch(deleteLDItem({ itemcode: item.itemcode, CCCode: selectedCC })).then((result) => {
            if (deleteLDItem.fulfilled.match(result)) {
                toast.success('Deleted Successfully');
                dispatch(fetchMainGrid({ ccode: selectedCC }));
            } else {
                toast.error(result.payload || 'Failed to delete item');
            }
        });
    };

    const handleSubmit = () => {
        if (!file) { toast.error('Upload Pdf File'); return; }
        if (file.name.split('.').pop().toLowerCase() !== 'pdf') { toast.error('Invalid Upload File. Upload only PDF File'); return; }
        if (!reportDate) { toast.error('Select Date'); return; }
        if (!userRemarks.trim()) { toast.error('Enter Remarks'); return; }
        if (!selectedCC) { toast.error('Select Cost Center'); return; }
        if (mainGridItems.length === 0) { toast.error('Please Add Items to Report'); return; }

        // Asset items (code starts with '1') must transfer whole units only.
        for (const item of mainGridItems) {
            const asset = isAssetCode(item.itemcode);
            const lost = num(item.Lost), damaged = num(item.Damaged);
            if (lost === 0 && damaged === 0) { toast.error('Enter Damaged or Lost Quantity'); return; }
            if (asset && (lost + damaged) > 1) { toast.error('Invalid Asset Qty'); return; }
            if (asset && (lost + damaged) !== 1) { toast.error('Invalid Asset Quantity'); return; }
        }
        if (!mainGridItems.every((i) => checkedRows[i.itemcode])) { toast.error('Verify Items'); return; }
        if (!mainGridItems.every((i) => stockTypeEdits[i.itemcode])) { toast.error('Select Stock Type'); return; }
        if (!mainGridItems.every((i) => (remarksEdits[i.itemcode] || '').trim())) { toast.error('Enter Remarks for ItemCodes'); return; }

        let icodes = '', ids = '', lqtys = '', dqtys = '', types = '', remarks = '', avlqtys = '';
        mainGridItems.forEach((item) => {
            const asset = isAssetCode(item.itemcode);
            const lost = num(item.Lost), damaged = num(item.Damaged);
            ids     += `${item.id},`;
            icodes  += `${item.itemcode},`;
            avlqtys += `${item.avlqty},`;
            types   += `${stockTypeEdits[item.itemcode]},`;
            remarks += `${(remarksEdits[item.itemcode] || '').trim()},`;
            // Assets report as whole units (1), matching the legacy asset-qty rule.
            lqtys += `${asset ? (lost !== 0 ? 1 : 0) : lost},`;
            dqtys += `${asset ? (damaged !== 0 ? 1 : 0) : damaged},`;
        });

        dispatch(submitLostOrDamaged({
            CCCode: selectedCC,
            itemcodes: icodes,
            Reporttype: '1',
            itemids: ids,
            Lostqtys: lqtys,
            Damangedqtys: dqtys,
            Stocktype: types,
            Remarks: remarks,
            AvlQtys: avlqtys,
            Date: formatDateForSP(reportDate),
            UserRemarks: userRemarks,
            Filechk: 'Yes',
            Extension: file.name.split('.').pop(),
        }));
    };

    const handleReset = () => {
        setSelectedCC('');
        setCategory(''); setSelectionType(''); setSearchText('');
        setLotQtyEdits({});
        setCurrentLotItemCode('');
        setCheckedRows({}); setStockTypeEdits({}); setRemarksEdits({});
        setReportDate(todayFormatted());
        setUserRemarks('');
        setFile(null);
        dispatch(clearMainGrid());
        dispatch(clearLotDetails());
        dispatch(clearSuggestions());
        dispatch(clearCategories());
        if (uid && roleId) dispatch(fetchCostCenters({ uid, rid: roleId }));
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const gridHasData = mainGridItems.length > 0;
    const checkedCount = mainGridItems.filter((i) => checkedRows[i.itemcode]).length;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center shadow">
                        <PackageX className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Lost Or Damaged Items</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Report lost or damaged stock items for a cost center.</p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── Cost Center selection ─────────────────────────────────────── */}
            <SectionCard icon={Building2} title="Cost Center" accent="indigo">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <Label required>CC / Job Code</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)}>
                                <option value="">-Please Select-</option>
                                {ccList.map((cc) => (
                                    <option key={cc.CCID} value={cc.CCID}>{cc.CCVAL}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.ccList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>
                    {loading.mainGrid && (
                        <div className="flex items-center gap-2 text-sm text-indigo-600"><SpinnerIcon /> Loading items…</div>
                    )}
                </div>
            </SectionCard>

            {/* ── Item search ──────────────────────────────────────────────── */}
            {selectedCC && (
                <SectionCard icon={Search} title="Search &amp; Add Item" accent="amber">
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="min-w-[200px]">
                            <Label required>Search By Category</Label>
                            <select className={inputCls} value={category} onChange={handleCategoryChange} disabled={loading.categoryList}>
                                <option value="">-Please Select-</option>
                                {categoryList.map((c) => (
                                    <option key={c.Categoryid} value={c.Categoryid}>{c.CategoryValue}</option>
                                ))}
                            </select>
                        </div>
                        <div className="min-w-[200px]">
                            <Label required>Search By Type</Label>
                            <select className={inputCls} value={selectionType} onChange={handleSelectionTypeChange} disabled={!category}>
                                <option value="">-Please Select-</option>
                                {SELECTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div className="relative flex-1 min-w-[260px]" ref={searchBoxRef}>
                            <Label required>Search Here</Label>
                            <input type="text" disabled={!selectionType} placeholder="Start typing to search…"
                                className={`${inputCls} uppercase`}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value.toUpperCase())}
                                onFocus={() => searchText && setShowSuggestions(true)} />
                            {loading.suggestions && (
                                <Loader2 className="absolute right-3 top-9 h-3.5 w-3.5 text-indigo-500 animate-spin" />
                            )}
                            {showSuggestions && suggestions.length > 0 && (
                                <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
                                    {suggestions.map((s, i) => (
                                        <button key={i} type="button" onClick={() => handlePickSuggestion(s)}
                                            className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-b border-gray-50 dark:border-gray-700 last:border-0">
                                            {s.SearchItem}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={handleViewLots} disabled={loading.lotDetails}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.lotDetails ? <SpinnerIcon /> : <Search className="w-4 h-4" />} View
                            </button>
                            <button onClick={handleClearSearch}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                                <X className="w-3.5 h-3.5" /> Clear
                            </button>
                        </div>
                    </div>
                </SectionCard>
            )}

            {/* ── Lot / temp grid ──────────────────────────────────────────── */}
            {lotDetails.length > 0 && (
                <SectionCard icon={Layers} title="Item Lots"
                    subtitle={`Lost: ${lotTotals.lost.toFixed(4)}  Damaged: ${lotTotals.damaged.toFixed(4)}  Total: ${lotTotals.total.toFixed(4)}`}
                    accent="purple">
                    <div className="overflow-x-auto rounded-lg border border-purple-200 dark:border-purple-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                                    {['Received Date', 'Item Code', 'Mrr/Ref No', 'PO/Indent No', 'Avl Qty', 'Lost Qty', 'Damaged Qty', 'Total'].map((h) => (
                                        <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {lotDetails.map((row, idx) => {
                                    const e = lotQtyEdits[row.TempId] || { lost: '', damaged: '' };
                                    return (
                                        <tr key={row.TempId ?? idx} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-purple-50/40 dark:bg-purple-900/10'}>
                                            <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.Tempdate}</td>
                                            <td className="px-2.5 py-2 whitespace-nowrap font-mono text-gray-800 dark:text-gray-200">{row.TempItemcode}</td>
                                            <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.Tempmrr}</td>
                                            <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.Temppono}</td>
                                            <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{row.TempQuantity}</td>
                                            <td className="px-1.5 py-1.5">
                                                <input type="number" min="0" step="0.0001"
                                                    className={`${inputCls} w-24 text-right`}
                                                    value={e.lost}
                                                    onChange={(ev) => handleLotQtyChange(row, 'lost', ev.target.value)} />
                                            </td>
                                            <td className="px-1.5 py-1.5">
                                                <input type="number" min="0" step="0.0001"
                                                    className={`${inputCls} w-24 text-right`}
                                                    value={e.damaged}
                                                    onChange={(ev) => handleLotQtyChange(row, 'damaged', ev.target.value)} />
                                            </td>
                                            <td className="px-2.5 py-2 text-right font-semibold text-purple-700 dark:text-purple-400">
                                                {lotRowTotal(row).toFixed(4)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={handleHideLots}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            Hide
                        </button>
                        <button onClick={handleAddToIssue} disabled={loading.addTemp}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50">
                            {loading.addTemp ? <SpinnerIcon /> : <Plus className="w-4 h-4" />} Add
                        </button>
                    </div>
                </SectionCard>
            )}

            {/* ── Main staged-items grid ───────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={Layers} title="Lost / Damaged Items"
                    subtitle={`${checkedCount} of ${mainGridItems.length} item${mainGridItems.length !== 1 ? 's' : ''} verified`}
                    accent="indigo">
                    <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                                    {['Chk', 'Item Code', 'Item Name', 'Specification', 'DCA', 'Sub DCA', 'Units', 'Avl Qty', 'Stock Type', 'Lost Qty', 'Damaged Qty', 'Remarks', ''].map((h) => (
                                        <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {mainGridItems.map((item, idx) => (
                                    <tr key={`${item.itemcode}_${idx}`} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-indigo-50/40 dark:bg-indigo-900/10'}>
                                        <td className="px-2.5 py-2">
                                            <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                checked={!!checkedRows[item.itemcode]} onChange={() => toggleRowChecked(item.itemcode)} />
                                        </td>
                                        <td className="px-2.5 py-2 whitespace-nowrap font-mono text-gray-800 dark:text-gray-200">{item.itemcode}</td>
                                        <td className="px-2.5 py-2 text-gray-900 dark:text-white">{item.itemname}</td>
                                        <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400">{item.specification}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{item.dcacode}</td>
                                        <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{item.subdcacode}</td>
                                        <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300">{item.units}</td>
                                        <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">
                                            {isAssetCode(item.itemcode) ? '1' : item.avlqty}
                                        </td>
                                        <td className="px-1.5 py-1.5 w-32">
                                            <select className={inputCls}
                                                value={stockTypeEdits[item.itemcode] || ''}
                                                onChange={(e) => setStockTypeEdits((p) => ({ ...p, [item.itemcode]: e.target.value }))}>
                                                <option value="">-Select-</option>
                                                <option value="New Stock">New Stock</option>
                                                <option value="Stock">Stock</option>
                                            </select>
                                        </td>
                                        <td className="px-2.5 py-2 text-right font-semibold text-rose-700 dark:text-rose-400">{item.Lost}</td>
                                        <td className="px-2.5 py-2 text-right font-semibold text-rose-700 dark:text-rose-400">{item.Damaged}</td>
                                        <td className="px-1.5 py-1.5 min-w-[160px]">
                                            <textarea rows={1} className={inputCls}
                                                value={remarksEdits[item.itemcode] || ''}
                                                onChange={(e) => setRemarksEdits((p) => ({ ...p, [item.itemcode]: e.target.value.replace(/,/g, '') }))} />
                                        </td>
                                        <td className="px-1.5 py-1.5">
                                            <button onClick={() => handleDeleteRow(item)} disabled={loading.deleteItem}
                                                className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 disabled:opacity-50">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label required>Upload Pdf File</Label>
                            <div className="relative">
                                <input type="file" accept=".pdf"
                                    className={`${inputCls} pl-9`}
                                    onChange={(e) => setFile(e.target.files?.[0] || null)} />
                                <Paperclip className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                            </div>
                        </div>
                        <div>
                            <Label required>Date</Label>
                            <CustomDatePicker value={reportDate} onChange={(d) => setReportDate(formatDateForSP(d))}
                                format="DD-MMM-YYYY" placeholder="Select Date" maxDate={new Date()} />
                        </div>
                        <div>
                            <Label required>Remarks</Label>
                            <textarea rows={2} className={inputCls} placeholder="Enter remarks…"
                                value={userRemarks} onChange={(e) => setUserRemarks(e.target.value)} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={handleReset}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            Reset
                        </button>
                        <button onClick={handleSubmit} disabled={loading.submit || uploading}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                            {(loading.submit || uploading) ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                            {loading.submit ? 'Submitting…' : uploading ? 'Uploading…' : 'Submit'}
                        </button>
                    </div>
                </SectionCard>
            )}
        </div>
    );
};

export default LostOrDamagedItems;
